<template lang="pug">
form(novalidate @submit.prevent="reviewBooking" ref="form").k-booking-review
  k-card-info
    .paragraph Please leave a review for your booking with #[span.brand-pink--text.font-weight-bold {{ reviewFor.fullName }}]
      |  on {{ $moment(booking.timestampUtc).tz(authenticatedUser.timezone).format('D MMMM YYYY') }}.
      |  Reviews are required for every session, so you can either do it now or before your next booking.
  .mb-10(v-if="reviewVideo")
    //- Review Video
    k-field-rating(
      v-model="fields.callQualityRating"
      i18n-key="review.callQualityRating"
      :error-messages="defaultErrorMessages('callQualityRating')"
      ref="callQualityRating"
      label-large
      bottom-hint
    ).mb-10

    //- Only low if video rating is
    k-field-textarea(
      v-if="fields.callQualityRating && fields.callQualityRating < 5"
      v-model="fields.callQualityComment"
      i18n-key="review.callQualityComment"
      :error-messages="defaultErrorMessages('callQualityComment')"
      counter
      large
      :rows="$vuetify.breakpoint.mdAndUp ? '2' : '5'"
      :maxlength="1000"
      ref="callQualityComment"
      label-large
    )

  template(v-if="canReviewAdvisor")
    k-field-rating(
      v-model="fields.rating"
      i18n-key="review.stars"
      :i18n-data="{ name: reviewFor.firstName }"
      :error-messages="defaultErrorMessages('rating')"
      ref="rating"
      label-large
    ).mb-10

    //- had to use breakpoint to match the designs since on desktop it is 2 lines and on mobile it is 5
    k-field-textarea(
      v-model="fields.comment"
      :i18n-key="'review.' + (authenticatedUser.id === booking.advisor.id ? 'commentLearner' : 'commentAdvisor' )"
      :i18n-data="{ name: reviewFor.firstName }"
      :error-messages="defaultErrorMessages('comment')"
      counter
      large
      :rows="$vuetify.breakpoint.mdAndUp ? '2' : '5'"
      :maxlength="1000"
      ref="comment"
      label-large
    )

  .d-inline-flex.flex-column.k-booking-review__submit.mt-10
    k-button(
      color="brand-pink"
      type="submit"
      :loading="isSubmittingForm"
      responsive
    ).white--text.ma-0 {{ $t('booking.review.button') }}
</template>

<script>
import { call } from 'vuex-pathify'
import { Validates, UsesAuthenticatedUser } from '@/mixins'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { Booking, Review, KintellCard } from '@/models'

export default {
  mixins: [
    Validates,
    UsesAuthenticatedUser
  ],
  props: {
    booking: {
      type: Booking,
      required: true,
    },
    reviewVideo: {
      type: Boolean,
      default: false
    },
    pageContext: {
      required: true,
      type: String
    }
  },
  validations () {
    const validations = {
      fields: {}
    }
    if (this.canReviewAdvisor) {
      validations.fields.rating = { required }
      validations.fields.comment = { minLength: minLength(10), maxLength: maxLength(1000) }
    }

    if (this.reviewVideo) {
      validations.fields.callQualityRating = { required }
      validations.fields.callQualityComment = { minLength: minLength(10), maxLength: maxLength(1000) }
    }
    return validations
  },
  data () {
    return {
      fields: {
        rating: null,
        comment: '',
        callQualityRating: null,
        callQualityComment: ''
      },
    }
  },
  computed: {
    card () {
      return new KintellCard(this.booking.kintellCard)
    },
    reviewFor () {
      return (this.authenticatedUser.id === this.booking.advisor.id)
        ? this.booking.learner
        : this.booking.advisor
    },
    // can't review advisor if card was unlisted
    canReviewAdvisor () {
      // If the user is on the review page then we want to check if the user has already reviewed the advisor on the kintell card
      if (this.pageContext === 'page') {
        return this.booking.kintellCard.status !== 'unlisted' && this.booking.stats.relatedCompletedBookingsCount < 1
      }
      // If the user has a card awaiting review we always want to show the fields for reviewing an author
      return this.booking.kintellCard.status !== 'unlisted'
    }
  },
  methods: {
    ...call({
      reloadBookings: 'myKintell/reloadBookings',
      refreshUser: 'auth/refresh'
    }),
    reviewBooking () {
      this.attemptFormApiSubmission(async () => {
        await new Review({
          comment: this.fields.comment,
          rating: this.fields.rating,
          callQualityRating: this.fields.callQualityRating,
          callQualityComment: this.fields.callQualityComment
        })
          .for(this.booking)
          .save()
        if (this.canReviewAdvisor) {
          this.$toast.success('Thanks for your review! We\'ll pass it on to ' + this.reviewFor.firstName + ' :)')
        } else {
          this.$toast.success('Thank you for your call quality feedback, we\'ll pass it on to our product team.')
        }

        await Promise.all([
          this.refreshUser(),
          this.reloadBookings()
        ])

        this.$router.push({ name: 'my-kintell-bookings-id-complete', params: { id: this.booking.id } })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.k-booking-review {
  &__submit {
    @include sm-and-down {
      display: flex !important;
    }
  }
}
</style>
